import React, { useEffect, useState } from 'react';
import { KeyboardAvoidingView, Platform, Pressable, ScrollView, Switch, Text, View } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { showError } from '../toast';
import { track } from '../analytics';
import { getSignupSource } from '../source';
import { promoLabel } from './ReputationScreen';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { api } from '../api';
import { useAuth } from '../AuthContext';
import { MapBackground } from '../map-background';
import { Button, Card, COLORS, Input, Label, Muted, Select, Title, Constrain } from '../ui';
import { HANDYMAN_SPECIALTIES } from '../constants';
import { joinRegions, RegionMultiPicker } from '../settlement-picker';
import { LegalLinks } from '../legal';
import { t } from '../i18n';

// Свободна регистрация: наемодател, пропърти мениджър, майстор.
// Наемател, собственик и обитател влизат САМО по покана — тогава ролята се
// разпознава автоматично (invitedAs) и изборът не се показва.
// Обитателят (RESIDENT) е като поканен наемател, но без ЕГН/адрес — той не е
// страна по договора и не получава документи.
//
// App Store (Guideline 3.1.1): регистрацията на наемодатели/пропърти мениджъри
// в iOS приложението е допустима, защото абонаментът им се предлага през
// Apple In-App Purchase (вж. SubscriptionScreen). Затова на всички платформи
// се показват и трите свободни роли.
// Функция, а не константа — етикетите се превеждат при рендиране (смяна на езика)
const getRoles = () =>
  [
    { key: 'LANDLORD', label: t('Наемодател'), hint: t('Отдавате собствени имоти') },
    { key: 'AGENCY', label: t('Пропърти мениджър'), hint: t('Управлявате чужди имоти (фирма)') },
    { key: 'HANDYMAN', label: t('Майстор'), hint: t('Получавате заявки за ремонти') },
  ] as const;

export default function RegisterScreen({ route }: any) {
  const navigation = useNavigation<any>();
  const { registrationToken, phone: invitedPhone, invitedAs, suggestedFirstName, suggestedLastName } =
    route.params ?? {};
  const { signIn } = useAuth();
  const ROLES = getRoles();

  // Два режима: ОБИКНОВЕНА регистрация (без SMS, паролата е задължителна)
  // и регистрация по покана след SMS код (registrationToken).
  const direct = !registrationToken;

  // Поканен номер (наемател/собственик) → ролята е ясна, не питаме.
  // По подразбиране — наемодател (на всички платформи).
  const [role, setRole] = useState<string>(invitedAs ?? 'LANDLORD');
  const [phone, setPhone] = useState('');
  const [firstName, setFirstName] = useState(suggestedFirstName ?? '');
  const [lastName, setLastName] = useState(suggestedLastName ?? '');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  // Физическо лице / фирма (пропърти мениджърът е винаги фирма)
  const [personType, setPersonType] = useState<'INDIVIDUAL' | 'COMPANY'>('INDIVIDUAL');
  const [companyName, setCompanyName] = useState('');
  const [eik, setEik] = useState('');
  const [mol, setMol] = useState('');
  const [vatNumber, setVatNumber] = useState('');
  // Майстор: области, в които работи (workArea е текстът им за съвместимост)
  const [workRegions, setWorkRegions] = useState<string[]>([]);
  const [specialties, setSpecialties] = useState<string[]>([]);
  const [tenantEgn, setTenantEgn] = useState('');
  const [idCardAddress, setIdCardAddress] = useState('');
  const [gdprConsent, setGdprConsent] = useState(false);
  // Бърза регистрация (от реклама): наемодател само с телефон + парола;
  // имената се питат на първия екран след влизане. „Още полета“ отваря всичко.
  const [showMore, setShowMore] = useState(false);
  const quick = direct && role === 'LANDLORD' && !showMore;
  // Активна промоция „първите N — X дни безплатно“ (само за свободна регистрация)
  const [promo, setPromo] = useState<{ trialDays: number; maxAccounts: number; remaining: number } | null>(null);
  useEffect(() => {
    if (!direct) return;
    track('register_view', { role });
    api('/auth/promotion').then((r) => setPromo(r.promotion)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [direct]);
  const [loading, setLoading] = useState(false);
  // Свободната регистрация минава през SMS код — телефонът се доказва,
  // за да не блокира някой чужд номер. 'form' → 'code' → регистрация.
  const [step, setStep] = useState<'form' | 'code'>('form');
  const [smsCode, setSmsCode] = useState('');

  // Стъпка 1 при свободна регистрация: код по SMS на въведения номер
  const requestCode = async () => {
    setLoading(true);
    try {
      await api('/auth/otp/request', { body: { phone, purpose: 'register' } });
      track('otp_sent', { role });
      setStep('code');
    } catch (e: any) {
      showError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const submit = async (tokenOverride?: string) => {
    setLoading(true);
    try {
      const body: Record<string, unknown> = {
        registrationToken: tokenOverride ?? registrationToken,
        role,
        ...(firstName.trim() ? { firstName: firstName.trim() } : {}),
        ...(lastName.trim() ? { lastName: lastName.trim() } : {}),
        gdprConsent,
        ...(email.trim() ? { email: email.trim() } : {}),
        ...(password ? { password } : {}),
      };
      const isCompany = role === 'AGENCY' || personType === 'COMPANY';
      if (role === 'LANDLORD' || role === 'HANDYMAN') Object.assign(body, { personType });
      if (isCompany) {
        Object.assign(body, { companyName, eik, mol, ...(vatNumber.trim() ? { vatNumber: vatNumber.trim() } : {}) });
      }
      if (role === 'HANDYMAN') {
        Object.assign(body, { workRegions, workArea: joinRegions(workRegions), specialties });
      }
      if (role === 'TENANT') {
        Object.assign(body, {
          ...(tenantEgn.trim() ? { tenantEgn: tenantEgn.trim() } : {}),
          ...(idCardAddress.trim() ? { idCardAddress: idCardAddress.trim() } : {}),
        });
      }
      const source = await getSignupSource();
      if (source) body.source = source;
      const res = await api('/auth/register', { body });
      track('registered', { role });
      // Наемодател/ПМ: веднага след регистрацията каним към отзиви за
      // минали наематели — пълни репутационната база от първия ден.
      if (role === 'LANDLORD' || role === 'AGENCY') {
        await AsyncStorage.setItem('pastReviewsPrompt', '1');
      }
      await signIn(res);
    } catch (e: any) {
      showError(e.message);
    } finally {
      setLoading(false);
    }
  };

  // Стъпка 2: кодът от SMS-а дава токен, с който регистрацията минава
  const verifyAndRegister = async () => {
    setLoading(true);
    try {
      const v = await api('/auth/otp/verify', { body: { phone, code: smsCode } });
      await submit(v.registrationToken);
    } catch (e: any) {
      showError(e.message);
      setLoading(false);
    }
  };

  if (step === 'code') {
    return (
      <KeyboardAvoidingView style={{ flex: 1 }} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
        <MapBackground />
        <ScrollView contentContainerStyle={{ padding: 16, paddingBottom: 140 }} keyboardShouldPersistTaps="handled">
          <Constrain>
            <View style={{ marginTop: 90 }}>
              <Title>{t('Потвърдете номера си')}</Title>
              <Card>
                <Muted>{t('Изпратихме 6-цифрен код на {phone}. Така се уверяваме, че номерът е ваш.', { phone })}</Muted>
                <Label>{t('SMS код')}</Label>
                <Input
                  placeholder="123456"
                  keyboardType="number-pad"
                  maxLength={6}
                  autoFocus
                  value={smsCode}
                  onChangeText={setSmsCode}
                />
                <Button
                  title={t('Потвърди и се регистрирай')}
                  onPress={verifyAndRegister}
                  disabled={smsCode.length !== 6}
                  loading={loading}
                />
                <Button title={t('Назад')} variant="ghost" onPress={() => setStep('form')} />
              </Card>
            </View>
          </Constrain>
        </ScrollView>
      </KeyboardAvoidingView>
    );
  }

  return (
    <KeyboardAvoidingView style={{ flex: 1 }} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
      <MapBackground />
      <ScrollView
        contentContainerStyle={{ padding: 16, paddingBottom: 140 }}
        keyboardShouldPersistTaps="handled"
        keyboardDismissMode="on-drag"
      >
      <Constrain>
      <Title>{t('Регистрация')}</Title>
      {direct ? (
        <Muted>{t('Бърза регистрация — за наемодатели, пропърти мениджъри и майстори.')}</Muted>
      ) : (
        <Muted>{t('Телефон: {phone}', { phone: invitedPhone })}</Muted>
      )}

      {direct && promo && (role === 'LANDLORD' || role === 'AGENCY') && (
        <Card style={{ marginTop: 12, backgroundColor: '#FFF4D6', borderColor: '#F5D48A' }}>
          <Text style={{ color: '#7a3e00', fontWeight: '800', fontSize: 16 }}>
            {t('🎁 Първите {n} регистрирани получават {trial} безплатно', { n: promo.maxAccounts, trial: promoLabel(promo.trialDays) })}
          </Text>
          <Text style={{ color: '#7a3e00', marginTop: 4 }}>
            {t('Остават {n} места — печелите го автоматично при регистрация.', { n: promo.remaining })}
          </Text>
        </Card>
      )}

      {invitedAs === 'TENANT' && (
        <Card style={{ marginTop: 12, borderColor: COLORS.primary }}>
          <Text style={{ color: COLORS.text, fontWeight: '600' }}>{t('Поканени сте като наемател 🏠')}</Text>
          <Muted>{t('Наемодателят ви е добавил с този номер. Остава само да въведете имената си.')}</Muted>
        </Card>
      )}
      {invitedAs === 'OWNER' && (
        <Card style={{ marginTop: 12, borderColor: COLORS.primary }}>
          <Text style={{ color: COLORS.text, fontWeight: '600' }}>{t('Поканени сте като собственик 🔑')}</Text>
          <Muted>{t('Пропърти мениджър управлява ваш имот. Остава само да въведете имената си.')}</Muted>
        </Card>
      )}
      {invitedAs === 'RESIDENT' && (
        <Card style={{ marginTop: 12, borderColor: COLORS.primary }}>
          <Text style={{ color: COLORS.text, fontWeight: '600' }}>{t('Поканени сте като обитател 🏠')}</Text>
          <Muted>
            {t('Наемодателят ви е добавил като живеещ в имота. Ще можете да подавате нередности за жилището. Остава само да въведете имената си.')}
          </Muted>
        </Card>
      )}

      {!invitedAs && quick && (
        // Бърза регистрация: ролята се избира с видими бутони (по подразбиране наемодател).
        // Друга роля → отваря се пълната форма с нужните за нея полета.
        <Card style={{ marginTop: 12 }}>
          <Label>{t('Регистрирам се като')}</Label>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 6 }}>
            {ROLES.map((r) => {
              const active = role === r.key;
              return (
                <Pressable
                  key={r.key}
                  onPress={() => setRole(r.key)}
                  style={{
                    paddingVertical: 10,
                    paddingHorizontal: 14,
                    borderRadius: 999,
                    borderWidth: 2,
                    borderColor: active ? COLORS.primary : COLORS.border,
                    backgroundColor: active ? COLORS.primary : 'transparent',
                  }}
                >
                  <Text style={{ fontSize: 15, fontWeight: '700', color: active ? '#fff' : COLORS.text }}>{r.label}</Text>
                </Pressable>
              );
            })}
          </View>
          <Muted style={{ marginTop: 8, fontSize: 12 }}>
            {t('Наемател сте? Не е нужна регистрация — наемодателят ви кани по телефон и влизате с SMS код.')}
          </Muted>
        </Card>
      )}
      {!invitedAs && !quick && (
      <Card style={{ marginTop: 12 }}>
        <Label>{t('Какъв сте?')}</Label>
        {ROLES.map((r) => (
          <Pressable
            key={r.key}
            onPress={() => setRole(r.key)}
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              paddingVertical: 10,
              borderBottomWidth: 1,
              borderBottomColor: COLORS.border,
            }}
          >
            <View
              style={{
                width: 20,
                height: 20,
                borderRadius: 10,
                borderWidth: 2,
                borderColor: role === r.key ? COLORS.primary : COLORS.muted,
                backgroundColor: role === r.key ? COLORS.primary : 'transparent',
                marginRight: 10,
              }}
            />
            <View style={{ flex: 1 }}>
              <Text style={{ fontSize: 16, color: COLORS.text }}>{r.label}</Text>
              <Text style={{ fontSize: 12, color: COLORS.muted }}>{r.hint}</Text>
            </View>
          </Pressable>
        ))}
      </Card>
      )}

      <Card>
        {direct && (
          <>
            <Label>{t('Телефон')}</Label>
            <Input
              value={phone}
              onChangeText={setPhone}
              keyboardType="phone-pad"
              placeholder="+359 88 812 3456"
              onFocus={() => track('register_phone_focus')}
            />
          </>
        )}
        {!quick && (
          <>
            <Label>{t('Име')}</Label>
            <Input value={firstName} onChangeText={setFirstName} placeholder={t('Иван')} />
            <Label>{t('Фамилия')}</Label>
            <Input value={lastName} onChangeText={setLastName} placeholder={t('Иванов')} />
            <Label>{t('Имейл (по избор)')}</Label>
            <Input
              value={email}
              onChangeText={setEmail}
              placeholder="ime@poshta.bg"
              autoCapitalize="none"
              keyboardType="email-address"
            />
          </>
        )}
        <Label>{t('Парола (мин. 8 знака)')}</Label>
        <Input value={password} onChangeText={setPassword} secureTextEntry placeholder="••••••••" />
        <Muted>{t('С нея влизате всеки път — по телефон или имейл.')}</Muted>
        {quick && (
          <Pressable onPress={() => setShowMore(true)}>
            <Muted style={{ marginTop: 8, color: COLORS.primary }}>{t('Искам да добавя име, имейл или фирма →')}</Muted>
          </Pressable>
        )}

        {role === 'TENANT' && (
          <>
            <Label>{t('ЕГН')}</Label>
            <Input value={tenantEgn} onChangeText={setTenantEgn} keyboardType="number-pad" placeholder={t('напр. 7523169263')} maxLength={10} />
            <Label>{t('Адрес по лична карта')}</Label>
            <Input value={idCardAddress} onChangeText={setIdCardAddress} placeholder={t('гр. …, ул. …, №')} />
            <Muted>{t('Нужни са за договор и фактура. ЕГН се пази криптирано и се вижда само от наемодателя.')}</Muted>
          </>
        )}

        {(role === 'LANDLORD' || role === 'HANDYMAN') && !quick && (
          // Повечето наемодатели са физически лица — фирмата е отметка, не отделна стъпка
          <Pressable
            onPress={() => setPersonType(personType === 'COMPANY' ? 'INDIVIDUAL' : 'COMPANY')}
            style={{ flexDirection: 'row', alignItems: 'center', marginTop: 14 }}
          >
            <View
              style={{
                width: 22,
                height: 22,
                borderRadius: 6,
                borderWidth: 2,
                borderColor: COLORS.primary,
                backgroundColor: personType === 'COMPANY' ? COLORS.primary : 'transparent',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {personType === 'COMPANY' && <Text style={{ color: '#fff', fontWeight: '800', fontSize: 14 }}>✓</Text>}
            </View>
            <Text style={{ marginLeft: 10, color: COLORS.text }}>{t('Регистрирам фирма (с ЕИК)')}</Text>
          </Pressable>
        )}

        {(role === 'AGENCY' || ((role === 'LANDLORD' || role === 'HANDYMAN') && personType === 'COMPANY')) && (
          <>
            <Label>{t('Име на фирмата')}</Label>
            <Input value={companyName} onChangeText={setCompanyName} placeholder={t('Имоти ЕООД')} />
            <Label>{t('ЕИК')}</Label>
            <Input value={eik} onChangeText={setEik} keyboardType="number-pad" placeholder="123456789" />
            <Label>{t('МОЛ')}</Label>
            <Input value={mol} onChangeText={setMol} placeholder={t('Иван Иванов')} />
            <Label>{t('ДДС номер (по избор)')}</Label>
            <Input value={vatNumber} onChangeText={setVatNumber} autoCapitalize="characters" placeholder="BG123456789" />
            <Muted>{t('Реквизитите влизат в договорите и фактурите.')}</Muted>
          </>
        )}

        {role === 'HANDYMAN' && (
          <>
            <Label>{t('Области, в които работите')}</Label>
            <RegionMultiPicker values={workRegions} onChange={setWorkRegions} />
            <Muted>{t('Ще получавате запитвания от всички населени места в избраните области.')}</Muted>
            <Label>{t('Какъв майстор сте? (може повече от едно)')}</Label>
            <Select
              placeholder={t('Изберете специалност…')}
              options={HANDYMAN_SPECIALTIES}
              values={specialties}
              multi
              onChange={setSpecialties}
            />
          </>
        )}

        <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 16 }}>
          <Switch value={gdprConsent} onValueChange={setGdprConsent} />
          <Text style={{ marginLeft: 10, flex: 1, color: COLORS.text }}>
            {t('Съгласен съм личните ми данни да се обработват за целите на приложението (GDPR).')}
          </Text>
        </View>
        <LegalLinks prefix={t('С регистрацията приемате')} />

        {/* Обвивката отчита всяко докосване, дори когато бутонът е неактивен —
            така виждаме кое поле спира хората */}
        <Pressable
          onPress={() => {
            const missing = [
              !quick && !firstName && 'име',
              !quick && !lastName && 'фамилия',
              password.length < 8 && 'парола',
              direct && phone.length < 6 && 'телефон',
              !gdprConsent && 'gdpr',
            ]
              .filter(Boolean)
              .join(',');
            track('register_submit_tap', { missing: missing || 'none', role });
          }}
        >
        <Button
          title={direct ? t('Продължи — код по SMS') : t('Регистрирай ме')}
          onPress={() => (direct ? requestCode() : submit())}
          disabled={
            (!quick && (!firstName || !lastName)) ||
            !gdprConsent ||
            password.length < 8 || // паролата е задължителна за всички
            (direct && phone.length < 6) ||
            ((role === 'AGENCY' || ((role === 'LANDLORD' || role === 'HANDYMAN') && personType === 'COMPANY')) &&
              (!companyName || !eik || !mol)) ||
            (role === 'HANDYMAN' && (workRegions.length === 0 || specialties.length === 0))
          }
          loading={loading}
        />
        </Pressable>
        <Muted style={{ textAlign: 'center', marginTop: 10 }}>{t('Безплатно · без карта · отнема 1 минута')}</Muted>
      </Card>
      {/* Изход от екрана, когато е отворен директно от линк (няма „назад“). */}
      <View style={{ alignItems: 'center', gap: 10, marginTop: 4, marginBottom: 8 }}>
        <Pressable onPress={() => navigation.navigate('Login')} hitSlop={8}>
          <Text style={{ color: COLORS.primary, fontWeight: '700' }}>{t('Вече имате акаунт? Вход')}</Text>
        </Pressable>
        <Pressable onPress={() => navigation.navigate('PublicReputation')} hitSlop={8}>
          <Text style={{ color: COLORS.primary, fontWeight: '700' }}>{t('Само проверка на наемател →')}</Text>
        </Pressable>
      </View>
      </Constrain>
      </ScrollView>
    </KeyboardAvoidingView>
  );
}
